import * as React from 'react';
import {CheckboxButtonView, IndeterminateIconView} from './Checkbox.styles';
import {CheckmarkIcon} from './CheckmarkIcon';
import type {
  SizeContextSize,
  HTMLAttributes,
  SpringTooling,
} from '../__SharedTypes';

export type CheckboxButtonProps = {
  colorVariant?: 'blue' | 'purple' | 'green';
  /**
   * Renders the state of the checkbox: true (checked), false (unchecked), or intermediate.
   */
  isChecked?: true | false | 'indeterminate';
  /**
   * Determines if the checkbox should render with error styling.
   */
  isError?: boolean;
  /**
   * Determines if the focus ring should render around the CheckboxButton
   */
  isFocused?: boolean;
  /**
   * Determines if the Checkbox should render in an indeterminate state. This takes precedence over isChecked.
   */
  isIndeterminate?: boolean;
  isDisabled?: boolean;
  /**
   * The size of the CheckboxButton.
   */
  size?: SizeContextSize;
} & HTMLAttributes &
  SpringTooling;

// Possible @SPRING_CLEANING/design debt: do we need this as a seprate component?
// @ts-expect-error - TS7031 - Binding element 'size' implicitly has an 'any' type.
const DashIcon = ({size}) => (
  <IndeterminateIconView style={{width: size}}>
    <svg
      fill="currentColor"
      viewBox="0 0 6 2"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M6 1C6 1.55 5.83125 2 5.625 2H0.375C0.16875 2 0 1.55 0 1C0 0.45 0.16875 0 0.375 0H5.625C5.83125 0 6 0.45 6 1Z" />
    </svg>
  </IndeterminateIconView>
);

// @ts-expect-error - TS7031 - Binding element 'isChecked' implicitly has an 'any' type. | TS7031 - Binding element 'isIndeterminate' implicitly has an 'any' type. | TS7031 - Binding element 'size' implicitly has an 'any' type.
export const CheckboxStateIndicator = ({isChecked, isIndeterminate}) => {
  if (!isChecked && !isIndeterminate) return null;

  if (isIndeterminate) return <DashIcon size={6} />;

  return <CheckmarkIcon />;
};

/**
 * An indicator for the Checkbox component.
 */
export const CheckboxButton = React.forwardRef<
  HTMLDivElement,
  CheckboxButtonProps
>(
  (
    {
      colorVariant = 'blue',
      isChecked = false,
      isError = false,
      isIndeterminate = false,
      isDisabled = false,
      ...props
    }: CheckboxButtonProps,
    forwardedRef
  ) => {
    return (
      <CheckboxButtonView
        {...props}
        colorVariant={colorVariant}
        isChecked={
          isChecked === true || isChecked === false ? isChecked : undefined
        }
        isDisabled={isDisabled}
        isError={isError}
        isIndeterminate={isIndeterminate}
        ref={forwardedRef}
      >
        <CheckboxStateIndicator
          isChecked={isChecked}
          isIndeterminate={isIndeterminate}
        />
      </CheckboxButtonView>
    );
  }
);

CheckboxButton.displayName = 'CheckboxButton';
CheckboxButton.toString = CheckboxButtonView.toString;
